import React from "react";
import {BannedUserDTO, ViewerGroupRequestDTO} from "@/generated-client";
import {Localizer} from "@/main/react/Types";
import DateFormatter from "@/main/common/DateFormatter";
import {CardMetadataBadges} from "@/main/react/card/CardMetadataBadges";
import AuthorAttribution from "@/main/react/component/AuthorAttribution";
import { getDisplayUsername, getUserProfilePath } from "@/main/common/UsernameDisplayUtils";
import type { BannedUserWithActorAvatar, ViewerGroupRequestWithResolverAvatar } from "@/main/common/UserAvatarContracts";
import { groupBanReasonLocalizationKey } from "@/main/common/GroupBanReasons";

export function ViewerRequestCardDisplayer(props: {
    request: ViewerGroupRequestDTO;
    localizer: Localizer;
    /** FI-614: the owner's own action on this row (the group page passes "Withdraw request"). */
    action?: React.ReactNode;
}) {
    const {request, localizer, action} = props;
    const df = new DateFormatter(localizer);

    const createdAt = request?.requestCreatedAt;
    const resolvedAt = request?.requestResolvedAt;
    const resolvedBy = request?.resolvedByUsername;
    const resolvedByProfileImageUrl = (request as ViewerGroupRequestWithResolverAvatar | null | undefined)?.resolvedByProfileImageUrl;
    const resolutionNote = request?.resolutionNote;
    const status = request?.requestStatus;
    const resolvedByDisplayUsername = getDisplayUsername(resolvedBy, localizer);
    const resolvedByProfileUrl = getUserProfilePath(resolvedBy, "discussions");

    return (
        <div className="mb-3 rounded-lg border p-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                <div className="min-w-0 flex-1">
                    <div className="space-y-2">
                        {createdAt && (
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
                                <div
                                    className="text-muted shrink-0 sm:w-36">{localizer("group.private.request_created_at")}</div>
                                <div className="min-w-0 flex-1">{df.timeSince(createdAt)}</div>
                            </div>
                        )}

                        {resolvedAt && (
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
                                <div
                                    className="text-muted shrink-0 sm:w-36">{localizer("group.private.request_resolved_at")}</div>
                                <div className="min-w-0 flex-1">{df.timeSince(resolvedAt)}</div>
                            </div>
                        )}

                        {resolvedBy && (
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
                                <div
                                    className="text-muted shrink-0 sm:w-36">{localizer("group.private.request_resolved_by")}</div>
                                <div className="min-w-0 flex-1">
                                    <AuthorAttribution localizer={localizer}
                                        name={resolvedByDisplayUsername}
                                        href={resolvedByProfileUrl}
                                        imageUrl={resolvedByProfileImageUrl}
                                        avatarSeed={resolvedBy}
                                        className="text-sm text-muted-foreground"
                                        avatarClassName="size-6"
                                    />
                                </div>
                            </div>
                        )}

                        {resolutionNote && (
                            <div className="mt-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
                                <div
                                    className="text-muted shrink-0 sm:w-36">{localizer("group.private.request_resolution_note")}</div>
                                <div className="min-w-0 flex-1">{resolutionNote}</div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex flex-col items-start gap-2 self-start sm:ml-2 sm:items-end">
                    <CardMetadataBadges items={[{key: "status", label: status}]} />
                    {action}
                </div>
            </div>
        </div>
    );
}

export function BannedUserCardDisplayer(props: {
    banned: BannedUserDTO;
    localizer: Localizer;
}) {
    const {banned, localizer} = props;
    const df = new DateFormatter(localizer);
    const bannedWithActorAvatar = banned as BannedUserWithActorAvatar | null | undefined;

    const bannedBy = banned?.bannedBy;
    const bannedByDisplayUsername = getDisplayUsername(bannedBy, localizer);
    const bannedByProfileUrl = getUserProfilePath(bannedBy, "discussions");
    // The structured reason type, never `banned.reason`. The free text is the owner's private
    // moderation note about this very viewer, and it is no longer sent to this surface at all
    // (group.jte stops emitting data-banned-reason). An absent or unknown type reads as
    // "no reason given" rather than guessing a label.
    const reasonKey = groupBanReasonLocalizationKey(banned?.reasonType);
    const bannedAt = banned?.bannedAt;
    const bannedUntil = banned?.bannedUntil;

    return (
        <div className="mb-3 rounded-lg border p-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                <div className="min-w-0 flex-1">
                    <div className="space-y-2 text-sm">
                        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
                            <div className="text-muted shrink-0 sm:w-36">{localizer("group.banned.banned_by")}</div>
                            <div className="min-w-0 flex-1">
                                {bannedBy ? (
                                    <AuthorAttribution localizer={localizer}
                                        name={bannedByDisplayUsername}
                                        href={bannedByProfileUrl}
                                        imageUrl={bannedWithActorAvatar?.bannedByProfileImageUrl}
                                        avatarSeed={bannedBy}
                                        className="text-sm text-muted-foreground"
                                        avatarClassName="size-6"
                                    />
                                ) : localizer("group.banned.unknown")}
                            </div>
                        </div>

                        <div className="mt-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
                            <div className="text-muted shrink-0 sm:w-36">{localizer("group.banned.reason")}</div>
                            <div className="min-w-0 flex-1">
                                {reasonKey ? localizer(reasonKey) : localizer("group.banned.no_reason")}
                            </div>
                        </div>

                        <div className="mt-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
                            <div className="text-muted shrink-0 sm:w-36">{localizer("group.banned.banned_at")}</div>
                            <div className="min-w-0 flex-1">
                                {bannedAt ? df.timeSince(bannedAt) : localizer("group.banned.unknown")}
                            </div>
                        </div>

                        <div className="mt-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
                            <div
                                className="text-muted shrink-0 sm:w-36">{localizer("group.banned.banned_until")}</div>
                            <div className="min-w-0 flex-1">
                                {bannedUntil ? df.timeSince(bannedUntil) :
                                    <span className="font-medium">{localizer("group.banned.permanent")}</span>}
                            </div>
                        </div>

                        <p className="mt-4 text-muted">
                            {localizer("group.banned.notice")}
                        </p>
                    </div>
                </div>

                <div className="self-start sm:ml-2">
                    <CardMetadataBadges items={[{key: "banned", label: localizer("group.banned.title")}]}/>
                </div>
            </div>
        </div>
    );
}
